import {
  Code,
  CodeBlock,
  Icon,
  InlineStack,
  Link,
  Panel,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Paragraph,
} from '@customerio/pluma-components/react';

const EMAIL_SNIPPET = `analytics.identify('john.doe@example.com', {
unsubscribed: false
});`;

const ID_SNIPPET = `analytics.identify('userId', {
email: 'john.doe@example.com',
unsubscribed: false,
});`;

type IdentifierListProps = {
  identifiers: readonly string[];
};

/**
 * The identifiers as `code`, joined the way Ember's `joinWordsAsSnippets` did:
 * commas between, "and" before the last.
 */
function IdentifierList({ identifiers }: IdentifierListProps) {
  return (
    <>
      {identifiers.map((identifier, index) => (
        <span key={identifier}>
          {index > 0 ? (index === identifiers.length - 1 ? ' and ' : ', ') : ''}
          <Code>{identifier}</Code>
        </span>
      ))}
    </>
  );
}

type SegmentHowItWorksProps = {
  /** The workspace identifies people by email only. */
  isEmailIdentified: boolean;
  /** The workspace identifies people by several identifiers. */
  isMultiIdentifier: boolean;
  /** The workspace's enabled identifiers, listed in the sentence as code. */
  enabledIdentifiers: readonly string[];
  workspaceSettingsHref: string;
};

/**
 * What to pass as Segment's `userId`, which depends on how the workspace
 * identifies people.
 *
 * Three configurations, and the reader only ever sees the one their workspace is
 * in — so all three have to be reproduced from the source rather than from what a
 * development workspace happens to render.
 */
export function SegmentHowItWorks({
  isEmailIdentified,
  isMultiIdentifier,
  enabledIdentifiers,
  workspaceSettingsHref,
}: SegmentHowItWorksProps) {
  return (
    <Panel>
      <PanelHeader>
        <PanelTitle level="3" text="product-h2">
          <InlineStack alignBlock="center" gap="100">
            <Icon name="tip" size="lg" color="subtle" />
            How it works
          </InlineStack>
        </PanelTitle>
      </PanelHeader>

      {isEmailIdentified ? (
        <PanelSection>
          <Paragraph mb="200">
            This workspace uses <Code>email</Code> as the identifier. Pass the email address as the Segment{' '}
            <Code>userId</Code>.
          </Paragraph>
          <CodeBlock language="javascript" code={EMAIL_SNIPPET} withLineNumbers withCopyButton={false} />
        </PanelSection>
      ) : isMultiIdentifier ? (
        <PanelSection>
          <Paragraph mb="200">
            This workspace has <IdentifierList identifiers={enabledIdentifiers} />{' '}
            <Link variant="secondary" href={workspaceSettingsHref}>
              enabled as {enabledIdentifiers.length === 1 ? 'an identifier' : 'identifiers'}
            </Link>
            . Pass a single identifier value as the UserId in your Segment identify call.
          </Paragraph>
          <CodeBlock language="javascript" code={EMAIL_SNIPPET} withLineNumbers withCopyButton={false} />
        </PanelSection>
      ) : (
        <PanelSection>
          <Paragraph mb="200">
            This workspace uses <Code>id</Code> as the identifier. The Segment <Code>userId</Code> maps to the
            Customer.io <Code>id</Code>.
          </Paragraph>
          <CodeBlock language="javascript" code={ID_SNIPPET} withLineNumbers withCopyButton={false} />
        </PanelSection>
      )}
    </Panel>
  );
}
